<template>
  <v-form ref="form" v-model="valid" @submit.prevent="sendOrder()">
    <input type="hidden" name="tenant" :value="tenant" />
    <input type="hidden" name="source" :value="source" />

    <v-row dense>
      <v-col cols="12" md="4" class="d-flex" style="flex-direction: column">
        <v-card class="mb-1">
          <v-card-text>
            <section
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.orderType.help')
              "
            >
              <label
                class="subtitle-1"
                v-text="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.orderType.label'
                  )
                "
              ></label>
              <v-radio-group v-model="$store.state.form.orderType">
                <v-radio
                  v-for="opt in orderTypes"
                  :key="opt.name"
                  :label="opt.title"
                  :value="opt.name"
                  :color="themeColor"
                  name="orderType"
                ></v-radio>
              </v-radio-group>
            </section>

            <section
              :aria-label="
                $vuetify.lang.t(
                  '$vuetify.inspectech.order.form.inspectionTypes.help'
                )
              "
            >
              <label
                class="subtitle-1"
                v-text="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.inspectionTypes.label'
                  )
                "
              ></label>
              <v-checkbox
                v-for="(opt, idx) in inspectionTypes"
                v-model="$store.state.form.types"
                :key="opt.value"
                :value="opt.value"
                :rules="[
                  (v) =>
                    (v && v.length > 0) ||
                    $vuetify.lang.t(
                      '$vuetify.inspectech.order.form.inspectionTypes.required'
                    ),
                ]"
                :hide-details="idx < inspectionTypes.length - 1"
                name="types"
                class="orderType mt-0"
                :color="themeColor"
              >
                <template v-slot:label>
                  <v-tooltip
                    right
                    open-delay="600"
                    max-width="400px"
                    color="info"
                  >
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ opt.text }}</span>
                    </template>
                    {{ opt.description || opt.text }}
                  </v-tooltip>
                </template>
              </v-checkbox>
            </section>
          </v-card-text>
        </v-card>

        <v-card class="mb-1 flex-grow-1">
          <v-card-text>
            <section
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.customer.help')
              "
            >
              <label
                class="subtitle-1"
                v-text="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.label'
                  )
                "
              ></label>

              <v-text-field
                v-if="showCompany"
                id="customer-company-name"
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.companyName.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.companyName.help'
                  )
                "
                v-model="$store.state.form.customer.companyName"
                :rules="nameRulesRelaxed"
                name="customerCompanyName"
              ></v-text-field>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    id="customer-first-name"
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.customer.firstName.label'
                      )
                    "
                    :hint="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.customer.firstName.help'
                      )
                    "
                    v-model="$store.state.form.customer.firstName"
                    required
                    :rules="nameRules"
                    name="customerFirstName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    id="customer-last-name"
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.customer.lastName.label'
                      )
                    "
                    :hint="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.customer.lastName.help'
                      )
                    "
                    v-model="$store.state.form.customer.lastName"
                    required
                    :rules="nameRules"
                    name="customerLastName"
                  ></v-text-field>
                </v-col>
              </v-row>

              <AddressFields
                prefix="customer"
                v-model="$store.state.form.customer.address"
                :rules="{
                  address: addressRulesRelaxed,
                  postalCode: postalCodeRulesRelaxed,
                  city: cityRulesRelaxed,
                }"
              />

              <v-text-field
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.phone.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.phone.help'
                  )
                "
                v-model="$store.state.form.customer.telephone"
                required
                :rules="telephoneRules"
                name="customerTelephone"
              ></v-text-field>

              <v-text-field
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.email.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.email.help'
                  )
                "
                v-model="$store.state.form.customer.email"
                required
                :rules="emailRules"
                name="customerEmail"
              ></v-text-field>

              <v-text-field
                v-if="showCustomerNumber"
                id="customer-number"
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.number.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.customer.number.help'
                  )
                "
                v-model="$store.state.form.customer.number"
                :rules="numberRulesRelaxed"
                name="customerNumber"
              ></v-text-field>
            </section>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="d-flex" style="flex-direction: column">
        <v-card class="mb-1" v-if="showBroker">
          <v-card-text>
            <section
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.broker.help')
              "
            >
              <label
                class="subtitle-1"
                v-text="
                  $vuetify.lang.t('$vuetify.inspectech.order.form.broker.label')
                "
              ></label>

              <v-text-field
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.broker.companyName.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.broker.companyName.help'
                  )
                "
                v-model="$store.state.form.broker.companyName"
                :rules="nameRulesRelaxed"
                name="brokerCompanyName"
              ></v-text-field>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    id="broker-first-name"
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.broker.firstName.label'
                      )
                    "
                    :hint="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.broker.firstName.help'
                      )
                    "
                    v-model="$store.state.form.broker.firstName"
                    :rules="nameRulesRelaxed"
                    name="brokerFirstName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    id="broker-last-name"
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.broker.lastName.label'
                      )
                    "
                    :hint="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.broker.lastName.help'
                      )
                    "
                    v-model="$store.state.form.broker.lastName"
                    :rules="nameRulesRelaxed"
                    name="brokerLastName"
                  ></v-text-field>
                </v-col>
              </v-row>

              <AddressFields
                v-if="false"
                v-model="$store.state.form.broker.address"
                :rules="{
                  address: addressRulesRelaxed,
                  postalCode: postalCodeRulesRelaxed,
                  city: cityRulesRelaxed,
                }"
                prefix="broker"
              />

              <v-text-field
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.broker.phone.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.broker.phone.help'
                  )
                "
                v-model="$store.state.form.broker.telephone"
                :rules="telephoneRulesRelaxed"
                name="brokerTelephone"
              ></v-text-field>

              <v-text-field
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.broker.email.label'
                  )
                "
                :hint="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.broker.email.help'
                  )
                "
                v-model="$store.state.form.broker.email"
                :rules="emailRulesRelaxed"
                name="brokerEmail"
              ></v-text-field>
            </section>
          </v-card-text>
        </v-card>

        <v-card class="mb-1 flex-grow-1">
          <v-card-text>
            <section
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.estate.help')
              "
            >
              <label
                class="subtitle-1"
                v-text="
                  $vuetify.lang.t('$vuetify.inspectech.order.form.estate.label')
                "
              ></label>

              <div>
                <a
                  href="#"
                  @click="copyAddress"
                  style="text-decoration: none"
                  v-text="
                    $vuetify.lang.t(
                      '$vuetify.inspectech.order.form.estate.copyAddress'
                    )
                  "
                ></a>
              </div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.estate.owner1.firstName.label'
                      )
                    "
                    v-model="$store.state.form.owner1.firstName"
                    name="ownerFirstName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.estate.owner1.lastName.label'
                      )
                    "
                    v-model="$store.state.form.owner1.lastName"
                    name="ownerLastName"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.estate.owner2.firstName.label'
                      )
                    "
                    v-model="$store.state.form.owner2.firstName"
                    name="owner2FirstName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.estate.owner2.lastName.label'
                      )
                    "
                    v-model="$store.state.form.owner2.lastName"
                    name="owner2LastName"
                  ></v-text-field>
                </v-col>
              </v-row>

              <AddressFields
                v-model="$store.state.form.objectAddress"
                prefix="object"
                :mandatory="['address', 'postalCode', 'city']"
                :rules="{
                  address: addressRules,
                  postalCode: postalCodeRules,
                  city: cityRules,
                }"
              />

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.estate.location.label'
                      )
                    "
                    v-model="$store.state.form.estateLocation"
                    required
                    :rules="[
                      (v) =>
                        (v && v.length > 2) ||
                        $vuetify.lang.t(
                          '$vuetify.inspectech.order.form.estate.location.required'
                        ),
                    ]"
                    name="estateLocation"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.estate.number.label'
                      )
                    "
                    v-model="$store.state.form.estateNumber"
                    required
                    :rules="[
                      (v) =>
                        (v && v.length > 5) ||
                        $vuetify.lang.t(
                          '$vuetify.inspectech.order.form.estate.number.required'
                        ),
                    ]"
                    hint="Xxxxx N:NNN"
                    name="estateNumber"
                  ></v-text-field>
                </v-col>
              </v-row>
            </section>

            <section>
              <v-row dense>
                <AttributeColumn
                  v-for="item in getInputs($store.state.form.orderType)"
                  :key="item.name"
                  :value="item"
                >
                  <component
                    :is="getAttributeComponent(item)"
                    :attribute="item"
                    :value="$store.state.attributes[item.name]"
                    :name="item.name"
                    :label="item.label"
                    :hint="item.help"
                    @input="updateAttribute(item.name, $event)"
                  ></component>
                </AttributeColumn>
              </v-row>
            </section>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="d-flex" style="flex-direction: column">
        <v-card class="mb-1">
          <v-card-text>
            <section
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.date.help')
              "
            >
              <v-date-picker
                v-model="$store.state.form.preferredDate"
                :events="events"
                :event-color="eventColors"
                :allowed-dates="allowedDates"
                :color="themeColor"
                first-day-of-week="1"
                locale="sv-se"
                full-width
              ></v-date-picker>

              <v-row dense>
                <v-col>
                  <v-text-field
                    :label="
                      $vuetify.lang.t(
                        '$vuetify.inspectech.order.form.date.label'
                      )
                    "
                    v-model="$store.state.form.preferredDate"
                    :rules="dateRules"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <p v-if="showCalendar" class="mr-1 text-md-right">
                    <a
                      :href="`${tenant}/calendar?date=${$store.state.form.preferredDate}`"
                      target="_blank"
                      v-text="
                        $vuetify.lang.t(
                          '$vuetify.inspectech.order.form.calendar.link.label'
                        )
                      "
                    ></a>
                  </p>
                </v-col>
              </v-row>

              <p class="caption">
                {{
                  $vuetify.lang.t(
                    "$vuetify.inspectech.order.form.date.disclaimer"
                  )
                }}
              </p>
            </section>
          </v-card-text>
        </v-card>

        <v-card class="mb-1 flex-grow-1">
          <v-card-text>
            <section
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.privacy.help')
              "
            >
              <v-checkbox
                name="gdpr"
                v-model="$store.state.form.gdpr"
                :label="
                  $vuetify.lang.t(
                    '$vuetify.inspectech.order.form.privacy.label'
                  )
                "
                persistent-hint
                required
                :color="themeColor"
                :rules="[
                  (v) =>
                    !!v ||
                    $vuetify.lang.t(
                      '$vuetify.inspectech.order.form.privacy.required'
                    ),
                ]"
              ></v-checkbox>

              <div v-if="!!legalLink" class="pl-8">
                <a
                  :href="legalLink"
                  target="_blank"
                  style="text-decoration: none"
                  v-text="
                    $vuetify.lang.t(
                      '$vuetify.inspectech.order.form.privacy.link'
                    )
                  "
                ></a>
              </div>
            </section>
          </v-card-text>

          <v-card-actions>
            <v-btn
              type="submit"
              :loading="sent"
              :disabled="sent"
              :aria-label="
                $vuetify.lang.t('$vuetify.inspectech.order.form.submit.help')
              "
              large
              block
              dark
              :color="buttonColor"
              v-text="buttonText"
            ></v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <div
      class="caption pl-1"
      v-text="$vuetify.lang.t('$vuetify.inspectech.order.form.mandatory')"
    ></div>
  </v-form>
</template>

<script>
/* eslint-disable no-console, no-unused-vars */
import moment from "moment";
import AddressFields from "./AddressFields";
import AttributeColumn from "@/components/AttributeColumn.vue";
import ShortTextAttribute from "@/components/ShortTextAttribute.vue";
import LongTextAttribute from "@/components/LongTextAttribute.vue";
import NumericAttribute from "@/components/NumericAttribute.vue";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,7}$/;

export default {
  name: "OrderForm",
  components: { AttributeColumn, AddressFields },
  props: {
    tenant: {
      type: String,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    source: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    valid: false,
    sent: false,

    nameRules: [
      (v) => !!v || "Namn är obligatoriskt",
      (v) => (v && v.length >= 2) || "Namn måste vara minst två tecken",
    ],

    twoFiftyFiveRules: [
      (v) => !v || v.length < 256 || "Du kan ange max 255 tecken i detta fält",
    ],

    telephoneRules: [
      (v) => !!v || "Telefon är obligatoriskt",
      (v) => (v && v.length > 7) || "Du måste ange ett giltigt telefonnummer",
      (v) => (v && v.length <= 32) || "Telefon får max vara 32 tecken",
      (v) =>
        /^[0-9+\-()\s]+$/.test(v) || "Du måste ange ett giltigt telefonnummer",
    ],
    emailRules: [
      (v) => !!v || "E-post är obligatoriskt",
      (v) => emailRegex.test(v) || "Du måste ange en giltig e-postadress",
      (v) => (v && v.length <= 80) || "E-postadressen får max vara 80 tecken",
    ],

    addressRules: [
      (v) => !!v || "Adress är obligatoriskt",
      (v) => (v && v.length >= 2) || "Adressen måste vara minst två tecken",
      (v) => (v && v.length <= 80) || "Adressen får max vara 80 tecken",
    ],

    postalCodeRules: [
      (v) => !!v || "Postnr är obligatoriskt",
      (v) => (v && v.length >= 5) || "Postnr måste vara minst fem tecken",
      (v) => (v && v.length <= 10) || "Postnr får max vara 10 tecken",
    ],

    cityRules: [
      (v) => !!v || "Ort är obligatoriskt",
      (v) => (v && v.length >= 2) || "Ort måste vara minst två tecken",
      (v) => (v && v.length <= 80) || "Orten får max vara 80 tecken",
    ],

    nameRulesRelaxed: [
      (v) => !v || v.length >= 2 || "Namnet måste vara minst två tecken",
      (v) => !v || v.length <= 80 || "Namnet får max vara 80 tecken",
    ],

    telephoneRulesRelaxed: [
      (v) => !v || v.length > 7 || "Du måste ange ett giltigt telefonnummer",
      (v) => !v || v.length <= 32 || "Telefon får max vara 32 tecken",
      (v) =>
        !v ||
        /^[0-9+\-()\s]+$/.test(v) ||
        "Du måste ange ett giltigt telefonnummer",
    ],
    emailRulesRelaxed: [
      (v) => !v || emailRegex.test(v) || "Du måste ange en giltig e-postadress",
      (v) => !v || v.length <= 80 || "E-postadressen får max vara 80 tecken",
    ],

    addressRulesRelaxed: [
      (v) => !v || v.length >= 2 || "Adressen måste vara minst två tecken",
      (v) => !v || v.length <= 80 || "Adressen får max vara 80 tecken",
    ],

    postalCodeRulesRelaxed: [
      (v) => !v || v.length >= 5 || "Postnr måste vara minst fem tecken",
      (v) => !v || v.length <= 10 || "Postnr får max vara 10 tecken",
    ],

    cityRulesRelaxed: [
      (v) => !v || v.length >= 2 || "Orten måste vara minst två tecken",
      (v) => !v || v.length <= 80 || "Orten får max vara 80 tecken",
    ],

    numberRulesRelaxed: [
      (v) => !v || v.length >= 10 || "Personnummer måste vara minst 10 tecken",
      (v) => !v || v.length <= 13 || "Personnummer får max vara 13 tecken",
    ],

    yearRules: [
      (v) => !v || /^[0-9]{4}$/.test(v) || "Du måste ange ett giltigt årtal",
    ],

    numericRules: [
      (v) => !v || /^[0-9]{1,3}$/.test(v) || "Du måste ange ett heltal (1-999)",
    ],

    formState: null,
  }),
  computed: {
    themeColor() {
      return this.$store.getters.setting("order.theme.color") || null;
    },
    buttonColor() {
      return this.formState || this.themeColor || "primary";
    },
    orderTypes() {
      return this.$store.state.orderTypes;
    },
    inspectionTypes() {
      return this.$store.state.inspectionTypes;
    },
    bookedDates() {
      return this.$store.getters.calendarEvents
        .map((event) => {
          const dates = [];
          const end = (event.end ? moment(event.end) : moment()).endOf("days");
          let date = (event.start ? moment(event.start) : moment()).startOf(
            "days"
          );
          while (date.isBefore(end)) {
            dates.push(date.format("YYYY-MM-DD"));
            date.add(1, "days");
          }
          return dates;
        })
        .flat();
    },
    showCalendar() {
      return this.$store.getters.setting("order.calendar.enabled") === "true";
    },
    showCalendarEvents() {
      return this.$store.getters.setting("order.calendar.events") === "true";
    },
    saturday() {
      return this.$store.getters.setting("order.calendar.saturday") === "true";
    },
    sunday() {
      return this.$store.getters.setting("order.calendar.sunday") === "true";
    },
    dateRules() {
      return [(v) => this.allowedDates(v) || "Datumet är inte bokningsbart"];
    },
    showCompany() {
      return this.$store.getters.setting("order.company.enabled") === "true";
    },
    showCustomerNumber() {
      return this.$store.getters.setting("order.personnr.enabled") === "true";
    },
    showBroker() {
      return this.$store.getters.setting("order.broker.enabled") === "true";
    },
    legalLink() {
      const text = this.$store.getters.setting("order.legal.text");
      if (text) {
        if (text.startsWith("http:") || text.startsWith("https:")) {
          return text.trim();
        } else {
          return `${this.tenant}/legal`;
        }
      }
      return null;
    },
    buttonText() {
      if (this.formState === "success") {
        return this.$vuetify.lang.t(
          "$vuetify.inspectech.order.form.submit.success"
        );
      } else if (this.formState === "error") {
        return this.$vuetify.lang.t(
          "$vuetify.inspectech.order.form.submit.error"
        );
      }
      return this.$vuetify.lang.t(
        "$vuetify.inspectech.order.form.submit.label"
      );
    },
  },
  methods: {
    getInputs(orderType) {
      if (orderType) {
        const list = this.$store.getters.orderForms[orderType];
        if (list) {
          return list;
        }
      }
      return [];
    },
    getAttributeComponent(item) {
      if (item.dataType === "INTEGER") {
        return NumericAttribute;
      } else {
        if (item.maxLength > 100) {
          return LongTextAttribute;
        }
      }
      return ShortTextAttribute;
    },
    updateAttribute(name, value) {
      this.$store.commit("setAttribute", { name, value });
    },
    events(date) {
      return this.showCalendarEvents ? this.bookedDates.includes(date) : false;
    },
    eventColors(date) {
      return "red";
    },
    allowedDates(date) {
      if (moment(date).isoWeekday() === 6 && !this.saturday) {
        return false;
      }
      if (moment(date).isoWeekday() === 7 && !this.sunday) {
        return false;
      }
      return true;
    },
    copyAddress() {
      this.$store.dispatch("setOwnerInfo", this.$store.getters.customer);
    },
    sendOrder() {
      const form = this.$refs.form;
      this.formState = null;
      if (form.validate() && !this.sent) {
        this.sent = true;
        const data = { ...this.$store.state.form };
        const attributes = this.getInputs(this.$store.state.form.orderType);
        const values = [];
        for (let i = 0; i < attributes.length; i++) {
          const attr = attributes[i];
          const value = this.$store.state.attributes[attr.name];
          if (value) {
            values.push({ scope: attr.scope, name: attr.name, value: value });
          }
        }
        data.attributes = values;

        this.$store
          .dispatch("saveOrder", data)
          .then((result) => {
            this.formState = "success";
            setTimeout(() => this.doAfterSuccess(result), 2000);
          })
          .catch((err) => {
            console.log(err);
            this.formState = "error";
            this.sent = false;
          });
      }
    },
    doAfterSuccess(result) {
      this.$store.dispatch("resetForm");
      this.$emit("success", result);
    },
  },
  mounted() {
    this.formState = null;
  },
};
</script>

<style scoped>
.orderType .error--text {
  margin-top: 14px;
}
</style>
